import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import React from 'react';
import { DEFAULT_PER_PAGE, PER_PAGE_OPTIONS } from './pagination-constants';

export interface PaginationData {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number | null;
  to: number;
  first_page_url: string;
  last_page_url: string;
  next_page_url: string | null;
  prev_page_url: string | null;
}

export interface PaginationProps {
  pagination: PaginationData | null;
  onPageChange: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  className?: string;
  showInfo?: boolean;
  showPerPageSelector?: boolean;
}

/**
 * Reusable Pagination Component
 *
 * Features:
 * - Navigate between pages
 * - Show current page and total pages
 * - Smart page number display with ellipsis
 * - Responsive design
 * - Accessible navigation
 * - Optional pagination info display
 */
export const PaginationComponent: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  onPerPageChange,
  className,
  showInfo = true,
  showPerPageSelector = true,
}) => {
  const { t } = useTranslation();

  const {
    current_page,
    last_page,
    total,
    from,
    to,
    prev_page_url,
    next_page_url,
    per_page,
  } =
    pagination ||
    ({
      current_page: 1,
      last_page: 1,
      total: 0,
      from: null,
      to: 0,
      prev_page_url: null,
      next_page_url: null,
      per_page: DEFAULT_PER_PAGE,
    } as PaginationData);

  // Check if we should hide pagination controls
  const shouldHidePaginationControls = total === 0 || last_page <= 1;

  // If nothing to show, return null
  if (shouldHidePaginationControls && !showInfo && !showPerPageSelector) {
    return null;
  }

  // Generate page numbers to display
  const generatePageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const delta = 2; // Number of pages to show around current page

    // Always show first page
    pages.push(1);

    // Add ellipsis if there's a gap after first page
    if (current_page - delta > 2) {
      pages.push('ellipsis');
    }

    // Add pages around current page
    for (
      let i = Math.max(2, current_page - delta);
      i <= Math.min(last_page - 1, current_page + delta);
      i++
    ) {
      pages.push(i);
    }

    // Add ellipsis if there's a gap before last page
    if (current_page + delta < last_page - 1) {
      pages.push('ellipsis');
    }

    // Always show last page if it's not page 1
    if (last_page > 1) {
      pages.push(last_page);
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= last_page && page !== current_page) {
      onPageChange(page);
    }
  };

  const handlePreviousClick = () => {
    if (prev_page_url && current_page > 1) {
      handlePageClick(current_page - 1);
    }
  };

  const handleNextClick = () => {
    if (next_page_url && current_page < last_page) {
      handlePageClick(current_page + 1);
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Pagination Info */}
      {showInfo && (
        <div className='flex items-center justify-between text-sm text-muted-foreground'>
          <div className='flex items-center gap-4'>
            <div>
              {total > 0
                ? t('common.pagination.showing', {
                    from: from || 0,
                    to: to || 0,
                    total,
                  })
                : t('common.pagination.noResults')}
            </div>
            {/* Per Page Selector */}
            {showPerPageSelector && onPerPageChange && (
              <div className='flex items-center gap-2'>
                <span>{t('common.pagination.perPage')}:</span>
                <Select
                  value={String(per_page || DEFAULT_PER_PAGE)}
                  onValueChange={value => onPerPageChange(Number(value))}
                >
                  <SelectTrigger className='w-[80px] h-8'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PER_PAGE_OPTIONS.map(option => (
                      <SelectItem key={option} value={option.toString()}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            {t('common.pagination.pageInfo', {
              current: current_page,
              total: last_page,
            })}
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {!shouldHidePaginationControls && (
        <Pagination>
          <PaginationContent>
            {/* Previous Button */}
            <PaginationItem>
              <PaginationPrevious
                onClick={handlePreviousClick}
                className={cn(
                  'cursor-pointer',
                  !prev_page_url || current_page <= 1
                    ? 'pointer-events-none opacity-50'
                    : 'hover:bg-accent'
                )}
                aria-label={t('common.pagination.previous')}
              />
            </PaginationItem>

            {/* Page Numbers */}
            {pageNumbers.map((page, index) => (
              <PaginationItem key={index}>
                {page === 'ellipsis' ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    onClick={() => handlePageClick(page)}
                    isActive={page === current_page}
                    className={cn(
                      'cursor-pointer',
                      page === current_page
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    )}
                    aria-label={t('common.pagination.goToPage', { page })}
                  >
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}

            {/* Next Button */}
            <PaginationItem>
              <PaginationNext
                onClick={handleNextClick}
                className={cn(
                  'cursor-pointer',
                  !next_page_url || current_page >= last_page
                    ? 'pointer-events-none opacity-50'
                    : 'hover:bg-accent'
                )}
                aria-label={t('common.pagination.next')}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
};

PaginationComponent.displayName = 'PaginationComponent';

export default PaginationComponent;
